import { useEffect, useState } from "react"
import { View, Pressable } from "react-native"
import Slider from "@react-native-community/slider"
import { BFColors } from "@/constants/Colors"
import { type FeatureOptionProps } from "@/constants/BuyBox"
import ChevronDown from "@/assets/icons/chevron-down.svg"
import ChevronUp from "@/assets/icons/chevron-up.svg"
import BFText, { H5 } from "@/components/standard/BFText"

interface FeatureSliderProps {
  title: string
  value: string | number | null
  options: FeatureOptionProps[]
  onValueChanged: (val: string | number | null) => void
}

const FeatureSlider: React.FC<FeatureSliderProps> = ({
  title,
  value,
  options,
  onValueChanged,
}: FeatureSliderProps) => {
  const [expanded, setExpanded] = useState<boolean>(false)
  const [sliderValue, setSliderValue] = useState<number>(0)

  useEffect(() => {
    const selectedIdx = options.findIndex((opt) => opt.value === value)
    setSliderValue(selectedIdx / options.length)
  }, [value, options])

  const onSliderChanged = (val: number) => {
    setSliderValue(val)
    const idx = Math.max(
      0,
      Math.min(options.length - 1, Math.round(val * options.length))
    )
    const option = options[idx]
    if (option.value !== value) {
      onValueChanged(option.value)
    }
  }

  const selectedIdx = options.findIndex((opt) => opt.value === value)
  const selectedOption = options[selectedIdx]

  return (
    <>
      <Pressable
        testID={"expand-feature-toggle"}
        className="flex flex-row justify-between h-[62px] items-center"
        onPress={() => {
          setExpanded(!expanded)
        }}
      >
        <H5 className="text-midnight-blue font-figtree-bold">{title}</H5>
        <View className="flex flex-row">
          <H5 className="text-calm-blue font-figtree-bold mr-2">
            {value === null ? "Any" : `${selectedOption?.name}+`}
          </H5>
          {expanded ? (
            <ChevronUp width={21} height={21} />
          ) : (
            <ChevronDown width={21} height={21} />
          )}
        </View>
      </Pressable>
      {expanded && (
        <View className="flex flex-1 mb-12 items-center">
          <View className="flex flex-1 w-[300px]">
            <Slider
              style={{ alignItems: "center" }}
              thumbImage={require("@/assets/slider-thumb.png")}
              minimumValue={0}
              maximumValue={1}
              value={selectedIdx / options.length}
              minimumTrackTintColor={BFColors.calmBlue}
              maximumTrackTintColor={BFColors.calmBlue}
              onValueChange={onSliderChanged}
            />
            <BFText
              className={`absolute text-calm-blue text-[12px] font-spacegrotesk top-[34px]`}
              style={{
                left: 300 * sliderValue - 30 * ((300 * sliderValue) / 300),
              }}
            >
              {selectedOption?.name}
            </BFText>
          </View>
        </View>
      )}
    </>
  )
}

export default FeatureSlider
